import { useCallback, useEffect, useRef, useState } from "react";
import { ICE_SERVERS } from "./constants";

// =====================================================
// useWebRTCCall — voice & video calling over WebRTC
// A self-contained state machine that owns the RTCPeerConnection, local/remote
// media streams and the full call lifecycle, using the shared Socket.IO
// connection purely for signaling (offer / answer / ICE / hang-up). Media is
// peer-to-peer and never touches the server.
//
// States: idle → calling → connecting → connected → (idle)
//         idle → ringing (incoming) → connecting → connected → (idle)
//
// Everything is cleaned up aggressively on end/reject/error/unmount so the
// camera + mic indicators always switch off. TURN (for cross-NAT) is picked up
// from ICE_SERVERS; STUN-only still works on simple networks.
// =====================================================

const INITIAL = {
  state: "idle", // idle | calling | ringing | connecting | connected
  type: "voice", // voice | video
  peerId: null,
  peerName: "",
  peerAvatar: null,
  error: "",
};

export default function useWebRTCCall({ socket, userId, username }) {
  const [call, setCall] = useState(INITIAL);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const pendingCandidates = useRef([]);
  const incomingOffer = useRef(null);
  const peerRef = useRef({ id: null, name: "" });
  const callRef = useRef(INITIAL);

  useEffect(() => {
    callRef.current = call;
  }, [call]);

  // ── teardown ────────────────────────────────────────
  const cleanup = useCallback(() => {
    if (pcRef.current) {
      try {
        pcRef.current.ontrack = null;
        pcRef.current.onicecandidate = null;
        pcRef.current.onconnectionstatechange = null;
        pcRef.current.close();
      } catch {
        /* ignore */
      }
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    pendingCandidates.current = [];
    incomingOffer.current = null;
    peerRef.current = { id: null, name: "" };
    setLocalStream(null);
    setRemoteStream(null);
    setMuted(false);
    setCameraOff(false);
  }, []);

  const resetToIdle = useCallback(() => {
    cleanup();
    setCall(INITIAL);
  }, [cleanup]);

  // ── build a peer connection wired to signaling ──────
  const createPeer = useCallback(
    (peerId) => {
      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

      pc.onicecandidate = (e) => {
        if (e.candidate) {
          socket.emit("iceCandidate", {
            toUserId: peerId,
            fromUserId: userId,
            candidate: e.candidate,
          });
        }
      };

      pc.ontrack = (e) => {
        setRemoteStream(e.streams[0]);
        setCall((c) => (c.state === "connected" ? c : { ...c, state: "connected" }));
      };

      pc.onconnectionstatechange = () => {
        const st = pc.connectionState;
        if (st === "failed" || st === "closed" || st === "disconnected") {
          // Give a brief grace for "disconnected" (can self-recover); treat
          // hard failures as the end of the call.
          if (st === "failed" || st === "closed") {
            setCall((c) =>
              c.state === "idle" ? c : { ...c, error: "Connection lost" }
            );
            resetToIdle();
          }
        }
      };

      pcRef.current = pc;
      return pc;
    },
    [socket, userId, resetToIdle]
  );

  const getMedia = useCallback(async (type) => {
    const constraints = {
      audio: true,
      video: type === "video" ? { facingMode: "user" } : false,
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    localStreamRef.current = stream;
    setLocalStream(stream);
    return stream;
  }, []);

  // ── OUTGOING CALL ───────────────────────────────────
  const startCall = useCallback(
    async (peer, type = "voice") => {
      if (callRef.current.state !== "idle") return;
      if (!peer?._id) return;
      peerRef.current = { id: String(peer._id), name: peer.name || "User" };
      setCall({
        state: "calling",
        type,
        peerId: String(peer._id),
        peerName: peer.name || "User",
        peerAvatar: peer.profilePic || null,
        error: "",
      });
      try {
        const stream = await getMedia(type);
        const pc = createPeer(String(peer._id));
        stream.getTracks().forEach((t) => pc.addTrack(t, stream));

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        socket.emit("callUser", {
          toUserId: String(peer._id),
          fromUserId: userId,
          fromName: username,
          callType: type,
          offer,
        });
      } catch (err) {
        setCall((c) => ({
          ...c,
          error:
            err?.name === "NotAllowedError"
              ? "Camera/microphone permission denied"
              : "Could not start the call",
        }));
        setTimeout(resetToIdle, 1500);
      }
    },
    [createPeer, getMedia, socket, userId, username, resetToIdle]
  );

  // ── INCOMING CALL: accept / reject ──────────────────
  const acceptCall = useCallback(async () => {
    if (callRef.current.state !== "ringing") return;
    const peerId = peerRef.current.id;
    const type = callRef.current.type;
    setCall((c) => ({ ...c, state: "connecting", error: "" }));
    try {
      const stream = await getMedia(type);
      const pc = createPeer(peerId);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      await pc.setRemoteDescription(
        new RTCSessionDescription(incomingOffer.current)
      );
      // Flush any ICE that arrived before the remote description was set.
      for (const c of pendingCandidates.current) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(c));
        } catch {
          /* ignore */
        }
      }
      pendingCandidates.current = [];

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      socket.emit("answerCall", {
        toUserId: peerId,
        fromUserId: userId,
        answer,
      });
    } catch (err) {
      socket.emit("rejectCall", { toUserId: peerId, fromUserId: userId });
      setCall((c) => ({
        ...c,
        error:
          err?.name === "NotAllowedError"
            ? "Camera/microphone permission denied"
            : "Could not answer the call",
      }));
      setTimeout(resetToIdle, 1500);
    }
  }, [createPeer, getMedia, socket, userId, resetToIdle]);

  const rejectCall = useCallback(() => {
    const peerId = peerRef.current.id;
    if (peerId) socket.emit("rejectCall", { toUserId: peerId, fromUserId: userId });
    resetToIdle();
  }, [socket, userId, resetToIdle]);

  const endCall = useCallback(() => {
    const peerId = peerRef.current.id;
    if (peerId) socket.emit("endCall", { toUserId: peerId, fromUserId: userId });
    resetToIdle();
  }, [socket, userId, resetToIdle]);

  // ── in-call controls ────────────────────────────────
  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const enabled = stream.getAudioTracks().some((t) => t.enabled);
    stream.getAudioTracks().forEach((t) => (t.enabled = !enabled));
    setMuted(enabled);
  }, []);

  const toggleCamera = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const enabled = stream.getVideoTracks().some((t) => t.enabled);
    stream.getVideoTracks().forEach((t) => (t.enabled = !enabled));
    setCameraOff(enabled);
  }, []);

  // ── SIGNALING SUBSCRIPTIONS ─────────────────────────
  useEffect(() => {
    if (!socket || !userId) return;

    // Every signal below belongs to ONE conversation. A late event from a call
    // that already ended (the other side hanging up twice, a reconnect replay)
    // must never tear down or mutate the call we are in NOW — that is what made
    // a second call straight after the first behave erratically.
    const isCurrentPeer = (id) =>
      id != null && String(id) === String(peerRef.current.id);

    const onIncoming = ({ fromUserId, fromName, callType, offer }) => {
      // Busy: already in a call → auto-decline the new one.
      if (callRef.current.state !== "idle") {
        socket.emit("rejectCall", { toUserId: fromUserId, fromUserId: userId });
        return;
      }
      incomingOffer.current = offer;
      peerRef.current = { id: String(fromUserId), name: fromName || "User" };
      setCall({
        state: "ringing",
        type: callType === "video" ? "video" : "voice",
        peerId: String(fromUserId),
        peerName: fromName || "User",
        peerAvatar: null, // caller's avatar isn't carried in the signal
        error: "",
      });
    };

    const onAnswered = async ({ fromUserId, answer }) => {
      const pc = pcRef.current;
      if (!pc || !isCurrentPeer(fromUserId)) return;
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        // Flush any ICE candidates from the callee that arrived (trickled) before
        // the answer set the remote description. Without this the caller silently
        // drops early candidates — fine on localhost, but a real cause of
        // intermittent "won't connect" over jittery networks.
        for (const cand of pendingCandidates.current) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(cand));
          } catch {
            /* ignore */
          }
        }
        pendingCandidates.current = [];
        // NEVER downgrade a call that is already connected. ontrack fires while
        // setRemoteDescription is still settling, so by the time we get here the
        // remote media has usually already moved us to "connected"; writing
        // "connecting" unconditionally stranded the CALLER there forever and,
        // because the remote <video> only mounts once connected, they never saw
        // or heard the callee.
        setCall((c) =>
          c.state === "connected" ? c : { ...c, state: "connecting" }
        );
      } catch (err) {
        console.error("[call] failed to apply answer:", err.message);
        setCall((c) => (c.state === "idle" ? c : { ...c, error: "Could not connect the call" }));
        setTimeout(resetToIdle, 1500);
      }
    };

    const onIce = async ({ fromUserId, candidate }) => {
      const pc = pcRef.current;
      if (!candidate || !isCurrentPeer(fromUserId)) return;
      if (!pc || !pc.remoteDescription) {
        pendingCandidates.current.push(candidate);
        return;
      }
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch {
        /* ignore */
      }
    };

    const onRejected = ({ fromUserId } = {}) => {
      if (!isCurrentPeer(fromUserId)) return;
      setCall((c) => (c.state === "idle" ? c : { ...c, error: "Call declined" }));
      setTimeout(resetToIdle, 1200);
    };

    const onEnded = ({ fromUserId } = {}) => {
      if (!isCurrentPeer(fromUserId)) return;
      resetToIdle();
    };

    // The peer's socket went away entirely (tab closed, reload, network drop).
    // Without this the surviving side sits on a dead RTCPeerConnection until
    // ICE finally gives up, which reads as a frozen call.
    const onPeerGone = ({ userId: goneId } = {}) => {
      if (!isCurrentPeer(goneId)) return;
      setCall((c) => (c.state === "idle" ? c : { ...c, error: "Call ended — connection lost" }));
      setTimeout(resetToIdle, 1200);
    };

    const onFailed = ({ reason }) => {
      const msg =
        reason === "offline"
          ? "User is offline"
          : reason === "not_allowed"
          ? "Call not allowed"
          : "Call failed";
      setCall((c) => (c.state === "idle" ? c : { ...c, error: msg }));
      setTimeout(resetToIdle, 1400);
    };

    socket.on("incomingCall", onIncoming);
    socket.on("callAnswered", onAnswered);
    socket.on("iceCandidate", onIce);
    socket.on("callRejected", onRejected);
    socket.on("callEnded", onEnded);
    socket.on("callPeerGone", onPeerGone);
    socket.on("callFailed", onFailed);

    return () => {
      socket.off("incomingCall", onIncoming);
      socket.off("callAnswered", onAnswered);
      socket.off("iceCandidate", onIce);
      socket.off("callRejected", onRejected);
      socket.off("callEnded", onEnded);
      socket.off("callPeerGone", onPeerGone);
      socket.off("callFailed", onFailed);
    };
  }, [socket, userId, resetToIdle]);

  // ── UNANSWERED-CALL TIMEOUT ─────────────────────────
  // An outgoing call that is never picked up used to ring forever, leaving the
  // caller with a live camera/mic and no way back except a reload. Give up
  // after 45s and tell the peer to stop ringing.
  useEffect(() => {
    if (call.state !== "calling" && call.state !== "ringing") return;
    const timer = setTimeout(() => {
      const peerId = peerRef.current.id;
      if (peerId) {
        socket.emit(call.state === "calling" ? "endCall" : "rejectCall", {
          toUserId: peerId,
          fromUserId: userId,
        });
      }
      setCall((c) => (c.state === "idle" ? c : { ...c, error: "No answer" }));
      setTimeout(resetToIdle, 1200);
    }, 45000);
    return () => clearTimeout(timer);
  }, [call.state, socket, userId, resetToIdle]);

  // Safety: release media if the component tree unmounts mid-call.
  useEffect(() => cleanup, [cleanup]);

  return {
    call,
    localStream,
    remoteStream,
    muted,
    cameraOff,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleCamera,
  };
}
